import { z } from 'zod'
import { carInputSchema } from './car.js'
import { idSchema } from './common.js'
import type { Car } from './car.js'
import type { JobcardDetail } from './jobcard.js'
import { optionalText } from './common.js'

/**
 * A diagnostic trouble code as an OBD reader hands it over.
 *
 * `description` is optional because cheap readers return the code alone, and a
 * bare "P0301" on the jobcard is still worth more than nothing.
 */
export const dtcSchema = z.object({
  code: z.string().trim().min(1).max(16),
  description: optionalText(200),
})
export type Dtc = z.infer<typeof dtcSchema>

/**
 * One scan of one car, flat, as a scanner would naturally produce it.
 *
 * Deliberately NOT the jobcard create shape. That shape asks the caller to
 * decide something a scanner cannot know — whether this car is already in the
 * garage, and therefore whether to send `mode: "existing"` with an id or
 * `mode: "manual"` with a snapshot. Answering that is the whole point of this
 * endpoint, so the scanner sends what it read and nothing more.
 */
export const intakeSchema = z.object({
  /**
   * The identity of the car. Everything else about matching depends on it, so
   * it is the one field worth sending even when nothing else is known.
   */
  vin: optionalText(32),
  /** Fallback identity when the reader cannot get a VIN. */
  plate: z.string().trim().max(20).default(''),

  brand: z.string().trim().max(60).default(''),
  model: z.string().trim().max(60).default(''),
  year: z.number().int().min(1900).max(2100).nullable().default(null),
  color: optionalText(40),
  engineSize: optionalText(20),
  fuelType: carInputSchema.shape.fuelType,

  odometerKm: z.number().int().min(0).max(10_000_000).nullable().default(null),

  /** Fault codes read from the car. Rendered into the jobcard's complaint. */
  dtcs: z.array(dtcSchema).max(100).default([]),

  /**
   * Free text appended after the codes. For anything the scanner knows that a
   * code does not express — a technician's note, a readiness-monitor summary.
   */
  notes: optionalText(1000),

  /**
   * Which device or app this came from, recorded on the jobcard's internal
   * notes. Makes an unexpected jobcard traceable to the thing that created it.
   */
  source: z.string().trim().max(60).default(''),

  /**
   * The customer, when whoever is holding the scanner already knows.
   *
   * A VIN the garage has seen before brings its owner along on its own, but a
   * NEW car cannot: nothing in the workshop knows whose it is, and without
   * this the jobcard has to be matched up by hand afterwards — which is the
   * job the scanner exists to remove.
   *
   * Wins over the owner inferred from the VIN, on the same principle applied
   * everywhere else here: naming the customer is a decision, and matching a
   * VIN is a guess.
   */
  clientId: idSchema.nullable().optional(),

  /**
   * Put the car in that customer's garage as well as on the jobcard.
   *
   * On by default, to match the checkbox in the app. Only ever fills a blank:
   * a car that already has an owner is never moved to someone else, because
   * that is a decision for a human looking at both records.
   */
  saveCarToClient: z.boolean().default(true),
})
export type IntakeRequest = z.infer<typeof intakeSchema>

/**
 * What the intake did, not merely what it returned.
 *
 * A scanner run twice against the same car must be able to tell "I opened a
 * job" from "there was already one open", because those mean different things
 * to the person holding the scanner.
 */
export interface IntakeResult {
  /** False when an already-open jobcard for this car was returned instead. */
  jobcardCreated: boolean
  /** False when the car was already known, by VIN or by plate. */
  carCreated: boolean
  /** Blank fields on the known car that this scan filled in. */
  carFieldsFilled: string[]
  /**
   * How the jobcard's customer was decided, so the app can say which happened
   * rather than leaving the user to work it out from the result:
   *
   *   'requested'  the clientId you sent
   *   'from_vin'   the owner already on record for this car
   *   'none'       nobody — the car is new and no customer was named
   *   'conflict'   you named a customer, and the car is on record as somebody
   *                ELSE's. Yours was used for the jobcard; the car was left
   *                where it is, and the two records now disagree about who
   *                owns this vehicle. Worth showing to a human.
   */
  clientSource: 'requested' | 'from_vin' | 'none' | 'conflict'
  /** True when this call put the car into the customer's garage. */
  carAssignedToClient: boolean
  jobcard: JobcardDetail
  car: Car | null
}

/**
 * Render fault codes and notes into the jobcard's complaint field.
 *
 * Shared rather than server-only so a caller can preview exactly what staff
 * will read before sending it.
 */
export function renderIntakeComplaint(input: Pick<IntakeRequest, 'dtcs' | 'notes'>): string {
  const parts: string[] = []

  if (input.dtcs.length > 0) {
    const codes = input.dtcs
      .map((dtc) => (dtc.description ? `${dtc.code} — ${dtc.description}` : dtc.code))
      .join('\n')
    parts.push(`OBD scan:\n${codes}`)
  }

  if (input.notes) parts.push(input.notes)

  return parts.join('\n\n')
}
